import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { combineLatest, Observable } from 'rxjs';
import { ApplianceDetailsFacade } from '@modules/appliance-details/appliance-details.facade';
import {
	ApplianceDetailsVisibilityService,
	ApplianceDetailsFieldsVisibility
} from '@modules/appliance-details/appliance-visibility.service';
import { IdName } from '@shared/models/id-name';
import { debounceTime, distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators';
import { ApplianceDetailsState, createInitialState } from '@modules/appliance-details/state/appliance-details.store';
import { ApplianceTypeEnums } from '@modules/appliance-details/models/appliance-type.enum';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, AsyncPipe } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import { TlkSlideToggleComponent } from '@itero/ui-toolkit-angular/slide-toggle';
import { APPLIANCE_TYPES } from '@modules/appliance-details/models/appliance-type.constants';
import { applianceOptionsArchClicked, applianceOptionTypeClicked } from '@core/services/logger/bi-actions';
import { BiLoggerService } from '@core/services/logger/bi-logger.service';
import { observeJawSelection } from '@shared/utils/jaw.util';
import { debounceDelay } from '@shared/models/consts';
import { SelectComponent } from '@shared/components/select/select.component';

@Component({
	selector: 'rx-appliance-details',
	templateUrl: './appliance-details.component.html',
	styleUrls: ['./appliance-details.component.scss'],
	providers: [ApplianceDetailsFacade],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		ReactiveFormsModule,
		SelectComponent,
		TlkSlideToggleComponent,
		MatDividerModule,
		NgIf,
		AsyncPipe,
		TranslateModule
	]
})
export class ApplianceDetailsComponent extends BaseDestroyableComponent implements OnInit {
	form = this.formBuilder.group<ApplianceDetailsState>(createInitialState());

	applianceTypes$ = this.facade.applianceTypes$;
	isReadonly$ = this.facade.isReadonly$;
	applianceFieldsVisibility$ = this.applianceDetailsVisibilityService.fields$;
	isApplianceUpperReadonly$: Observable<boolean> = this.facade.isApplianceUpperReadonly$;
	isApplianceLowerReadonly$: Observable<boolean> = this.facade.isApplianceLowerReadonly$;

	/** Emits hidden fields patch when visibility changes. Readonly prevents clearing. */
	private readonly clearHiddenFieldsOnVisibilityChange$: Observable<Partial<ApplianceDetailsState>> = combineLatest([
		this.facade.isReadonly$,
		this.applianceDetailsVisibilityService.fields$
	]).pipe(
		distinctUntilChanged(
			(
				[prevReadOnly, prevVis]: [boolean, ApplianceDetailsFieldsVisibility],
				[currReadOnly, currVis]: [boolean, ApplianceDetailsFieldsVisibility]
			) =>
				prevReadOnly === currReadOnly &&
				prevVis.applianceType === currVis.applianceType &&
				prevVis.upperLowerCard === currVis.upperLowerCard
		),
		map(([isReadOnly, visibility]) => {
			if (isReadOnly) {
				return {};
			}

			const patch: Partial<ApplianceDetailsState> = {};

			if (!visibility.applianceType) {
				patch.applianceType = { Id: ApplianceTypeEnums.Empty, Name: '' };
			}
			if (!visibility.upperLowerCard) {
				patch.isUpperChecked = false;
				patch.isLowerChecked = false;
			}

			return patch;
		})
	);

	get isUpperControl() {
		return this.form.controls.isUpperChecked;
	}

	get isLowerControl() {
		return this.form.controls.isLowerChecked;
	}

	get applianceTypeControl() {
		return this.form.controls.applianceType;
	}

	constructor(
		private readonly formBuilder: FormBuilder,
		private readonly facade: ApplianceDetailsFacade,
		private readonly biLogger: BiLoggerService,
		private readonly applianceDetailsVisibilityService: ApplianceDetailsVisibilityService
	) {
		super();
	}

	ngOnInit(): void {
		this.subscribeToUserChanges();
		this.subscribeToUserBiEvents();
		this.loadFromStore();
		this.subscribeToStoreChanges();
		this.subscribeToClearHiddenFields();
	}

	private loadFromStore(): void {
		this.form.patchValue(this.facade.storeState, { emitEvent: false });
	}

	private subscribeToUserChanges(): void {
		this.form.valueChanges
			.pipe(
				distinctUntilChanged(),
				tap(() => {
					// @ts-expect-error - TODO: remove this and fix the type error
					const result = this.facade.updateStore(this.form.getRawValue());

					this.form.patchValue(result.formValues, { emitEvent: false });
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private subscribeToStoreChanges(): void {
		this.facade
			.subscribeToStoreChanges()
			.pipe(
				distinctUntilChanged(),
				tap(state => {
					this.form.patchValue(state, { emitEvent: false });
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private subscribeToUserBiEvents(): void {
		this.applianceTypeControl.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.componentAlive$)).subscribe(({ Id }: IdName) => {
			const selectedType = APPLIANCE_TYPES.find(type => type.Id === Id);

			if (selectedType) {
				this.biLogger.userActionEvent(applianceOptionTypeClicked(selectedType.Name));
			}
		});

		observeJawSelection(this.isUpperControl as FormControl<boolean>, this.isLowerControl as FormControl<boolean>)
			.pipe(debounceTime(debounceDelay), distinctUntilChanged(), takeUntil(this.componentAlive$))
			.subscribe(selectedOption => this.biLogger.userActionEvent(applianceOptionsArchClicked(selectedOption)));
	}

	private subscribeToClearHiddenFields(): void {
		this.clearHiddenFieldsOnVisibilityChange$
			.pipe(
				tap(patch => {
					if (Object.keys(patch).length > 0) {
						this.form.patchValue(patch);
					}
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}
}
